import { useState } from "react";
import { Pencil, Save, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "not set");

const FIELDS = [
  { key: "start_date", label: "Start" },
  { key: "end_date", label: "End" },
  { key: "deadline", label: "Deadline" },
];

export const DeadlineEditor = ({ gameweek, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = () => {
    setValues(Object.fromEntries(FIELDS.map((f) => [f.key, toLocalInput(gameweek[f.key])])));
    setErr("");
    setEditing(true);
  };

  const save = async () => {
    const payload = {};
    FIELDS.forEach(({ key }) => {
      if (values[key] && values[key] !== toLocalInput(gameweek[key])) payload[key] = new Date(values[key]).toISOString();
    });
    if (!Object.keys(payload).length) return setEditing(false);
    if (values.start_date && values.end_date && new Date(values.start_date) > new Date(values.end_date)) {
      return setErr("Start must be before end.");
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.patch(`/gameweeks/${gameweek.id}`, payload);
      setEditing(false);
      onSaved(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="text-xs text-[#AAB7C7] mb-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="gw-dates-display">
        <span>Start: <span className="text-white font-medium" data-testid="gw-start-value">{fmt(gameweek.start_date)}</span></span>
        <span>End: <span className="text-white font-medium" data-testid="gw-end-value">{fmt(gameweek.end_date)}</span></span>
        <span>Deadline: <span className="text-white font-medium" data-testid="gw-deadline-value">{fmt(gameweek.deadline)}</span></span>
        <button type="button" onClick={start} data-testid="gw-deadline-edit-btn"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#132A45] hover:bg-[#1B4276] text-[#C7F500] font-semibold transition-colors">
          <Pencil className="w-3 h-3" /> Change dates
        </button>
        <span className="basis-full">Leave kickoff blank to inherit the deadline.</span>
      </div>
    );
  }

  return (
    <div className="mb-3 p-3 rounded-xl bg-[#071426] border border-[#C7F500]/40 space-y-3" data-testid="gw-deadline-editor">
      <div className="grid sm:grid-cols-3 gap-2">
        {FIELDS.map(({ key, label }) => (
          <label key={key} className="text-xs text-[#AAB7C7] space-y-1">
            <span className="block uppercase tracking-wider">{label}</span>
            <input type="datetime-local" value={values[key] || ""} onChange={(e) => setValues({ ...values, [key]: e.target.value })}
              data-testid={key === "deadline" ? "gw-deadline-input" : `gw-${key.replace("_date", "")}-input`}
              className="w-full bg-[#0D2038] border border-[#1F3A5C] rounded-lg px-3 py-1.5 text-sm text-white focus:border-[#C7F500] focus:outline-none" />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={save} disabled={busy} data-testid="gw-deadline-save-btn"
          className="px-3 py-1.5 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-50 text-[#071426] text-xs font-semibold inline-flex items-center gap-1.5 transition-colors">
          <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save dates"}
        </button>
        <button type="button" onClick={() => setEditing(false)} data-testid="gw-deadline-cancel-btn"
          className="px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1F3A5C] text-xs font-semibold inline-flex items-center gap-1.5 transition-colors">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <span className="text-[11px] text-[#AAB7C7]">Changing the deadline moves every match this week; moving it later reopens picks.</span>
      </div>
      {err && <p className="text-xs text-red-400" data-testid="gw-deadline-error">{err}</p>}
    </div>
  );
};
